/** API helpers for institution endpoints */

export function createInstitutionApi(api) {
  return {
    getMyMemberships: () => api.get('/institutions/me'),
    createInstitution: (data) => api.post('/institutions', data),
    getInstitution: (id) => api.get(`/institutions/${id}`),
    updateInstitution: (id, data) => api.patch(`/institutions/${id}`, data),
    getDashboard: (id) => api.get(`/institutions/${id}/dashboard`),
    getAnalytics: (id) => api.get(`/institutions/${id}/analytics`),
    listMembers: (id) => api.get(`/institutions/${id}/members`),
    addMember: (id, data) => api.post(`/institutions/${id}/members`, data),
    updateMember: (id, memberId, data) => api.patch(`/institutions/${id}/members/${memberId}`, data),
    resendInvite: (id, memberId) => api.post(`/institutions/${id}/members/${memberId}/resend-invite`),
    inviteMember: (id, data) => api.post(`/institutions/${id}/members/invite`, data),
    listCohorts: (id) => api.get(`/institutions/${id}/cohorts`),
    createCohort: (id, data) => api.post(`/institutions/${id}/cohorts`, data),
    updateCohort: (id, cohortId, data) => api.patch(`/institutions/${id}/cohorts/${cohortId}`, data),
    deleteCohort: (id, cohortId) => api.delete(`/institutions/${id}/cohorts/${cohortId}`),
    assignCohortStudents: (id, cohortId, { userIds = [], emails = [] } = {}) =>
      api.post(`/institutions/${id}/cohorts/${cohortId}/students`, { user_ids: userIds, emails }),
    listCohortStudents: (id, cohortId) =>
      api.get(`/institutions/${id}/cohorts/${cohortId}/students`),
    listAssignments: (id) => api.get(`/institutions/${id}/assignments`),
    createAssignment: (id, data) => api.post(`/institutions/${id}/assignments`, data),
    updateAssignment: (id, assignmentId, data) =>
      api.patch(`/institutions/${id}/assignments/${assignmentId}`, data),
    startAssignment: (id, assignmentId) =>
      api.post(`/institutions/${id}/assignments/${assignmentId}/start`),
    getAssignmentReview: (id, assignmentId) =>
      api.get(`/institutions/${id}/assignments/${assignmentId}/review`),
    listMocks: (id) => api.get(`/institutions/${id}/mocks`),
    createMock: (id, data) => api.post(`/institutions/${id}/mocks`, data),
    updateMock: (id, mockId, data) => api.patch(`/institutions/${id}/mocks/${mockId}`, data),
    deleteMock: (id, mockId) => api.delete(`/institutions/${id}/mocks/${mockId}`),
    getPortalPublic: (slug) => api.get(`/institutions/portal/by-slug/${slug}/public`),
    getPortal: (slug) => api.get(`/institutions/portal/by-slug/${slug}`),
    previewInvite: (token) => api.get(`/institutions/invites/${token}`),
    acceptInvite: (token) => api.post(`/institutions/invites/${token}/accept`),
    getBilling: (id) => api.get(`/institutions/${id}/billing`),
    getBillingPlans: (id, currency = 'AUD') => api.get(`/institutions/${id}/billing/plans`, { params: { currency } }),
    createBillingCheckout: (id, data) => api.post(`/institutions/${id}/billing/checkout`, data),
    openBillingPortal: (id, data) => api.post(`/institutions/${id}/billing/portal`, data),
    syncBillingSeats: (id) => api.post(`/institutions/${id}/billing/sync-seats`),
    listQuestionSets: (id, status) =>
      api.get(`/institutions/${id}/question-sets`, { params: status ? { status } : {} }),
    getQuestionSet: (id, setId) => api.get(`/institutions/${id}/question-sets/${setId}`),
    previewQuestionSet: (id, setId) => api.post(`/institutions/${id}/question-sets/${setId}/preview`),
    replaceStimulusImage: (id, setId, stimulusId, file) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(
        `/institutions/${id}/question-sets/${setId}/stimuli/${stimulusId}/replace-image`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    replaceOptionImage: (id, setId, questionId, optionId, file) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(
        `/institutions/${id}/question-sets/${setId}/questions/${questionId}/options/${optionId}/replace-image`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    cropStimulusFromSource: (id, setId, stimulusId, bbox) =>
      api.post(
        `/institutions/${id}/question-sets/${setId}/stimuli/${stimulusId}/crop-from-source`,
        { bbox },
      ),
    cropOptionFromSource: (id, setId, questionId, optionId, bbox) =>
      api.post(
        `/institutions/${id}/question-sets/${setId}/questions/${questionId}/options/${optionId}/crop-from-source`,
        { bbox },
      ),
    addStimulusFromSource: (id, setId, bbox, label) =>
      api.post(`/institutions/${id}/question-sets/${setId}/stimuli/add-from-source`, {
        bbox,
        label,
      }),
    replaceSourceImage: (id, setId, file) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(
        `/institutions/${id}/question-sets/${setId}/replace-source`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    extractQuestionSet: (id, file, section) => {
      const form = new FormData();
      form.append('file', file);
      if (section) form.append('section', section);
      return api.post(`/institutions/${id}/question-sets/extract`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    updateQuestionSet: (id, setId, data) => api.put(`/institutions/${id}/question-sets/${setId}`, data),
    approveQuestionSet: (id, setId) => api.post(`/institutions/${id}/question-sets/${setId}/approve`),
    deleteQuestionSet: (id, setId) => api.delete(`/institutions/${id}/question-sets/${setId}`),
  };
}

export const STAFF_ROLES = ['owner', 'admin', 'tutor'];

export function isInstitutionStaff(role) {
  return STAFF_ROLES.includes(role);
}

export const PLAN_LABELS = {
  free: 'Free',
  tutor: 'Tutor',
  studio: 'Studio',
  centre: 'Centre',
  enterprise: 'Enterprise',
};

export const ASSIGNMENT_TYPES = [
  { value: 'question_set', label: 'Question set' },
  { value: 'custom_set', label: 'Institution question set' },
  { value: 'mini_mock', label: 'Mini mock' },
  { value: 'full_mock', label: 'Full mock' },
  { value: 'section_practice', label: 'Section practice' },
  { value: 'category_practice', label: 'Category practice' },
];

export const TIMING_MODES = [
  { value: 'timed', label: 'Timed' },
  { value: 'untimed', label: 'Untimed' },
  { value: 'ucat_style', label: 'UCAT-style' },
];

export const UCAT_SECTIONS = ['VR', 'DM', 'QR', 'SJT'];

export const SECTION_COLORS = {
  VR: '#3B82F6',
  DM: '#10B981',
  QR: '#F59E0B',
  SJT: '#8B5CF6',
};
